<template>
  <ax-dialog ref="dialog"
             :title="`Editing ${api}`"
             icon="mdi-pencil-outline"
             tooltip="Edit well"
             size="large"
             persistent
             :disabled="disabled"
             @submitted="onSubmit"
             @shown="onShown">
    <template slot="content">
      <v-card-text v-if="well">
        <v-row>
          <v-col cols="auto mb-3">
            <v-subheader class="ps-0"
                         style="height:auto;">Targets</v-subheader>
            <v-chip-group v-model="target"
                          @change="plotSingleWell"
                          mandatory
                          color="primary">
              <v-chip v-for="t in targets"
                      :key="t.value"
                      :value="t.value">{{ t.text }}</v-chip>
            </v-chip-group>
          </v-col>
          <v-col cols="auto mb-3">
            <v-subheader class="ps-0"
                         style="height:auto;">Methods</v-subheader>
            <v-chip-group v-model="method"
                          @change="plotSingleWell"
                          mandatory
                          color="secondary"
                          column>
              <v-chip v-for="m in methods"
                      :key="m.value"
                      :value="m.value">{{ m.text }}</v-chip>
            </v-chip-group>
          </v-col>
          <v-col>
            <v-subheader class="ps-0"
                         style="height:auto;">Techniques</v-subheader>
            <v-chip-group v-model="technique"
                          @change="plotSingleWell"
                          mandatory
                          color="warning">
              <v-chip v-for="t in techniques"
                      :key="t.value"
                      :value="t.value">{{ t.text }}</v-chip>
            </v-chip-group>
          </v-col>
        </v-row>
        <v-divider />
        <v-toolbar flat
                  tile
                  dense
                  class="px-0"
                  v-if="well">
          <v-toolbar-title>Estimated Ultimate Recovery: {{ totalEUR | numberFormat(round = true) }} ({{ eurUnits }})</v-toolbar-title>
          <v-spacer></v-spacer>
          <ax-toggle icon="mdi-trending-down"
                    tooltip="Toggle termination"
                    @clicked="showTermination = !showTermination" />
          <ax-toggle icon="mdi-magnify"
                    tooltip="Toggle termination"
                    @clicked="zoom = !zoom" />
        </v-toolbar>
        <vue-plotly id="editing"
                    class="mb-5"
                    :data="plots"
                    :layout="layout"
                    :options="options" />
        <v-row>
          <v-col>
            <v-text-field type="number" filled
                          prefix="Q =" hide-details
                          label="Initial production"
                          v-model.number="well.parameters[`Q_${method}_${target}_${technique}`]"
                          hide-spin-buttons @change="plotSingleWell" />
          </v-col>
          <v-col>
            <v-text-field type="number" filled
                          prefix="D =" hide-details
                          label="Initial decline"
                          v-model.number="well.parameters[`D_${method}_${target}_${technique}`]"
                          hide-spin-buttons @change="plotSingleWell" />
          </v-col>
          <v-col>
            <v-text-field type="number" filled
                          prefix="B =" hide-details
                          label="Hyperbolic exponent"
                          v-model.number="well.parameters[`B_${method}_${target}_${technique}`]"
                          hide-spin-buttons @change="plotSingleWell" />
          </v-col>
        </v-row>
      </v-card-text>
    </template>
    <template slot="submit_text"> Save </template>
  </ax-dialog>
</template>
<script>
import Predictions from '@/api/predictions'
import VuePlotly from '@statnett/vue-plotly'
import AxDialog from '@/components/dialog'
import AxToggle from '@/components/toggle'
import AxButton from '@/components/button'
export default {
  name: "dlgEditWell",
  props: {
    uuid: { type: String, required: true },
    api: { type: String, required: true },
    targets: { type: Array, required: true },
    methods: { type: Array, required: true },
    techniques: { type: Array, required: true },
    settings: { type: Object, required: true },
    termination: { type: Object, required: false },
    disabled: { type: Boolean, default: false },
  },
  components: {
    VuePlotly,
    AxDialog, AxToggle, AxButton
  },
  data() {
    return {
      debug: false,
      // logAxis: true,
      // showPredictions: true,
      zoom: false,
      showTermination: false,
      well: null,

      target: null,
      method: null,
      technique: null,

      parameters: {},
      decline: [],
      plots: [],
      layout: {
        showlegend: true,
        width: 868,
        height: 250,
        legend: { x: 1, y: 1, xanchor: 'right' },
        font: { family: "Roboto, sans-serif" },
        xaxis: { title: { text: "Time (Months)" }, dtick: 12 },
        yaxis: { title: { text: "Production" }, type: "log" },
        hovermode: "closest",
        margin: { l: 60, t: 10, r: 10, b: 60 },
        paper_bgcolor: "#ffffff",
        plot_bgcolor: "#ffffff",
      },
      options: { displayModeBar: false, responsive: true },
      colors: {
        arps1: "#F44336",
        advanced3a: "#2196F3",
        advanced5: "#2196F3",
        multi3a: "#FF9800",
        multi5: "#FF9800",
        hyper5: "#9C27B0",
        hyper5a: "#1B5E20",
      },
    }
  },
  watch: {
    showTermination: function () {
      this.plotSingleWell()
    },
    zoom: function () {
      this.plotSingleWell()
    },
  },
  computed: {
    months() {
      return this.last_valid_index(this.well.production[this.target])
    },
    totalProduction() {
      return this.sum(this.well.production[this.target])
    },
    totalPrediction() {
      return this.sum(this.well.predictions[`${this.target}_${this.method}`])
    },
    totalForecast() {
      return this.sum(this.well.forecasts[`${this.target}_${this.method}_${this.technique[0]}`])
    },
    totalPlot() {
      return this.plots.map(item => this.sum(item.y)).reduce((a, b) => a + b, 0)
    },
    totalEUR() {
      if (!!this.showTermination) return this.totalForecast
      return this.totalPlot
    },
    eurUnits() {
      return this.targets.find(item => item.value == this.target).units
    },
    targetColor() {
      return this.targets.find(item => item.value == this.target).color
    },
    methodColor() {
      return this.colors[this.method]
    },
    techniqueLabels() {
      return {
        Q: `Q_${this.method}_${this.target}_${this.technique}`,
        D: `D_${this.method}_${this.target}_${this.technique}`,
        B: `B_${this.method}_${this.target}_${this.technique}`,
        A: `A_${this.method}_${this.target}_${this.technique}`
      }
    },
  },
  methods: {
    onShown(val) {
      if (val) {
        this.target = this.settings['target']
        this.method = this.settings['method']
        this.technique = this.settings['technique']

        Predictions.get_well(this.uuid, this.api).then(response => {
          this.well = response.data
          this.well.parameters[`D_${this.method}_${this.target}_${this.technique}`] = this.roundTo(this.well.parameters[`D_${this.method}_${this.target}_${this.technique}`], 5)
          // Object.entries(this.well.parameters).forEach(([key,value]) => {
          //   this.$set(this.parameters, key, parseFloat(value.toFixed(6)))
          // })
          // this.getDeclines()
          this.plotSingleWell()
        })
      }
    },
    onSubmit() {
      this.well.parameters.index = this.api
      this.$refs.dialog.show = false
      this.$emit('submitted', JSON.parse(JSON.stringify(this.well.parameters)))
    },
    getProduction() {
      return this.well.production[`${this.target}`]
    },
    getPredictions() {
      return this.well.predictions[`${this.target}_${this.method}`]
    },
    getDeclines() {
      if (this.showTermination) {
        return this.well.forecasts[`${this.target}_${this.method}_${this.technique[0]}`]
      }
      return this.old_declineWell(
        600,
        this.well.parameters[this.techniqueLabels['Q']],
        this.well.parameters[this.techniqueLabels['D']],
        this.well.parameters[this.techniqueLabels['B']],
        this.well.parameters[this.techniqueLabels['A']],
      )
    },
    plotSingleWell() {
      // Reset existing data
      const target = this.target
      const method = this.method
      const technique = this.technique

      this.plots = []

      // Populate production
      this.plots.push({
        name: 'Production',
        type: "bar",
        x: this.create_array(1, this.months),
        y: this.getProduction(),
        marker: { color: this.targetColor, opacity: 0.5 },
      })

      // Populate predictions
      if (method != 'arp1') {
        this.plots.push({
          name: 'Prediction',
          type: 'scatter',
          mode: 'lines',
          // visible: this.showPredictions,
          x: this.create_array(this.months + 1, this.months + 12),
          y: this.getPredictions(),
          line: { color: this.methodColor, shape: 'spline', dash: 'dot', width: 1 },
        })
      }

      // // Hide curves from beginning to EOL
      let decline = this.getDeclines()
      let predictions = this.well.predictions[`${target}_${method}`].filter(val => val > 0)
      let predLength = this.last_valid_index(predictions)
      
      decline = Array(this.months + predLength).fill(null).concat(decline.slice(this.months + predLength))
      const timeline = this.zoom ? this.create_array(1, 600) : this.create_array(1, this.months + 24)

      this.plots.push({
        name: 'Decline',
        type: 'scatter',
        mode: 'lines',
        x: timeline,
        y: decline,
        line: { color: this.methodColor, shape: 'spline', dash: 'solid', width: 1 },
      })

      // this.layout.yaxis.type = this.logAxis ? 'log' : 'linear'
    },
    old_declineWell(months, Q, D, B, A) {
      return Array.from(Array(months), (d, i) => i + 1).map(t => this.old_arps(t, Q, D, B, A))
    },
    old_arps(t, Q, D, B, A) {
      if (B == 0) { // Exponential decline
        return Q * Math.exp(-D * (t-A))
      } else if (B == 1) { // Harmonic decline
        return Q / (1 + D * (t-A))
      } else { // Hyperbolic decline
        return Q / ((1 + B * D * (t-A)) ** (1 / B))
      }
    },
    roundTo(num, digits) {
      const factor = Math.pow(10, digits);
      return Math.round(num * factor) / factor;
    }
  }
}
</script>